import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronDownIcon, PlusIcon, CheckIcon, SearchIcon, ChevronLeftIcon } from 'lucide-react';
interface AuthenticationFiltersProps {
  deduplication: 'deduplicated' | 'raw';
  onDeduplicationChange: (value: 'deduplicated' | 'raw') => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  cardCountries: string[];
  onCardCountriesChange: (countries: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  additionalFilters: string[];
  onAdditionalFiltersChange: (filters: string[]) => void;
}
type MoreFiltersView = 'menu' | 'currency' | 'retry-status';
export function AuthenticationFilters({
  deduplication,
  onDeduplicationChange,
  cardTypes,
  onCardTypesChange,
  cardCountries,
  onCardCountriesChange,
  cardBrands,
  onCardBrandsChange,
  interactionTypes,
  onInteractionTypesChange,
  additionalFilters,
  onAdditionalFiltersChange
}: AuthenticationFiltersProps) {
  const [showDeduplicationDropdown, setShowDeduplicationDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showCardCountryDropdown, setShowCardCountryDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showMoreFiltersDropdown, setShowMoreFiltersDropdown] = useState(false);
  const [moreFiltersView, setMoreFiltersView] = useState<MoreFiltersView>('menu');
  // Shared payment filters (internal state)
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [showPMCategoryDropdown, setShowPMCategoryDropdown] = useState(false);
  const [showPMMethodDropdown, setShowPMMethodDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  // Additional filter states
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [retryStatuses, setRetryStatuses] = useState<string[]>([]);
  const deduplicationRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const cardCountryRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const moreFiltersRef = useRef<HTMLDivElement>(null);
  // Shared payment filter refs
  const pmCategoryRef = useRef<HTMLDivElement>(null);
  const pmMethodRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deduplicationRef.current && !deduplicationRef.current.contains(event.target as Node)) {
        setShowDeduplicationDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (cardCountryRef.current && !cardCountryRef.current.contains(event.target as Node)) {
        setShowCardCountryDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(event.target as Node)) {
        setShowMoreFiltersDropdown(false);
        setMoreFiltersView('menu');
      }
      if (pmCategoryRef.current && !pmCategoryRef.current.contains(event.target as Node)) {
        setShowPMCategoryDropdown(false);
      }
      if (pmMethodRef.current && !pmMethodRef.current.contains(event.target as Node)) {
        setShowPMMethodDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const availableAdditionalFilters = [{
    id: 'currency',
    label: 'Currency'
  }, {
    id: 'retry-status',
    label: 'Retry status'
  }];
  // Card countries with Malaysia first, then alphabetically
  const cardCountryOptions = ['Thailand', 'Malaysia', 'Afghanistan', 'Åland Islands', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Australia', 'Singapore', 'United Kingdom', 'United States'];
  // Currency options with LKR first, then alphabetically
  const currencyOptions = ['THB - Thai Baht', 'AED - United Arab Emirates Dirham', 'AFN - Afghan Afghani', 'ALL - Albanian Lek', 'AMD - Armenian Dram', 'ANG - Netherlands Antillean Guilder', 'AOA - Angolan Kwanza', 'ARS - Argentine Peso', 'AUD - Australian Dollar', 'SGD - Singapore Dollar', 'USD - US Dollar'];
  const handleMoreFilterClick = (filterId: string) => {
    setMoreFiltersView(filterId as MoreFiltersView);
  };
  const handleBackToMenu = () => {
    setMoreFiltersView('menu');
  };
  const handleApplyFilter = (applyFn: () => void) => {
    applyFn();
    setShowMoreFiltersDropdown(false);
    setMoreFiltersView('menu');
  };
  return <>
      {/* Main Filters */}
      <div className="relative" ref={deduplicationRef} data-id="element-634">
        <button onClick={() => setShowDeduplicationDropdown(!showDeduplicationDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-635">
          <span className="capitalize" data-id="element-636">{deduplication}</span>
          <ChevronDownIcon size={16} data-id="element-637" />
        </button>

        {showDeduplicationDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-638">
            <button onClick={() => {
          onDeduplicationChange('deduplicated');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'deduplicated' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-639">
              <div className="flex-1 min-w-0" data-id="element-640">
                <div className="text-sm font-medium text-gray-900" data-id="element-641">
                  Deduplicated
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-642">
                  Only includes the final outcome of failed payments that are
                  retried.
                </div>
              </div>
              {deduplication === 'deduplicated' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-643" />}
            </button>

            <button onClick={() => {
          onDeduplicationChange('raw');
          setShowDeduplicationDropdown(false);
        }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${deduplication === 'raw' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-644">
              <div className="flex-1 min-w-0" data-id="element-645">
                <div className="text-sm font-medium text-gray-900" data-id="element-646">Raw</div>
                <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-647">
                  Includes all payment attempts. Shows repeat attempts to
                  complete a payment.
                </div>
              </div>
              {deduplication === 'raw' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-648" />}
            </button>
          </div>}
      </div>

      {/* Secondary Filters */}
      <div className="w-full flex flex-wrap items-center gap-2" data-id="element-649">
        {/* Payment method category */}
        <div className="relative" ref={pmCategoryRef} data-id="element-650">
          <button onClick={() => setShowPMCategoryDropdown(!showPMCategoryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-651">
            <PlusIcon size={14} data-id="element-652" />
            <span data-id="element-653">Payment method category</span>
            {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-654">
                {paymentMethodCategories.length}
              </span>}
          </button>
          {showPMCategoryDropdown && <CompactDropdown title="Filter by: Category" options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
          setPaymentMethodCategories(selected);
          setShowPMCategoryDropdown(false);
        }} onBack={null} data-id="element-655" />}
        </div>

        {/* Payment method */}
        <div className="relative" ref={pmMethodRef} data-id="element-656">
          <button onClick={() => setShowPMMethodDropdown(!showPMMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-657">
            <PlusIcon size={14} data-id="element-658" />
            <span data-id="element-659">Payment method</span>
            {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-660">
                {paymentMethods.length}
              </span>}
          </button>
          {showPMMethodDropdown && <CompactDropdown title="Filter by: Payment method" options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
          setPaymentMethods(selected);
          setShowPMMethodDropdown(false);
        }} onBack={null} data-id="element-661" />}
        </div>

        {/* Funding source type */}
        <div className="relative" ref={fundingSourceRef} data-id="element-662">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-663">
            <PlusIcon size={14} data-id="element-664" />
            <span data-id="element-665">Funding source type</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-666">
                {fundingSourceTypes.length}
              </span>}
          </button>
          {showFundingSourceDropdown && <CompactDropdown title="Filter by: Funding source" options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          setFundingSourceTypes(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-667" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-668">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-669">
            <PlusIcon size={14} data-id="element-670" />
            <span data-id="element-671">Card type</span>
          </button>

          {showCardTypeDropdown && <CompactDropdown title="Filter by: Card type" options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-672" />}
        </div>

        <div className="relative" ref={cardCountryRef} data-id="element-673">
          <button onClick={() => setShowCardCountryDropdown(!showCardCountryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-674">
            <PlusIcon size={14} data-id="element-675" />
            <span data-id="element-676">Card country</span>
          </button>

          {showCardCountryDropdown && <SearchableDropdown title="Filter by: card country" options={cardCountryOptions} selectedOptions={cardCountries} onApply={selected => {
          onCardCountriesChange(selected);
          setShowCardCountryDropdown(false);
        }} data-id="element-677" />}
        </div>

        <div className="relative" ref={cardBrandRef} data-id="element-678">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-679">
            <PlusIcon size={14} data-id="element-680" />
            <span data-id="element-681">Card brand</span>
          </button>

          {showCardBrandDropdown && <CompactDropdown title="Filter by: Card brand" options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-682" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-683">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-684">
            <PlusIcon size={14} data-id="element-685" />
            <span data-id="element-686">Interaction type</span>
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title="Filter by: Interaction type" options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-687" />}
        </div>

        <div className="relative" ref={moreFiltersRef} data-id="element-688">
          <button onClick={() => {
          setShowMoreFiltersDropdown(!showMoreFiltersDropdown);
          setMoreFiltersView('menu');
        }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-689">
            <PlusIcon size={14} data-id="element-690" />
            <span data-id="element-691">More filters</span>
          </button>

          {showMoreFiltersDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-692">
              {moreFiltersView === 'menu' && <>
                  {availableAdditionalFilters.map(filter => <button key={filter.id} onClick={() => handleMoreFilterClick(filter.id)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-693">
                      <PlusIcon size={14} className="text-blue-600" data-id="element-694" />
                      <span data-id="element-695">{filter.label}</span>
                    </button>)}
                </>}

              {moreFiltersView === 'currency' && <SearchableDropdown title="Filter by: Currency" options={currencyOptions} selectedOptions={currencies} onApply={selected => handleApplyFilter(() => setCurrencies(selected))} onBack={handleBackToMenu} isInline data-id="element-696" />}

              {moreFiltersView === 'retry-status' && <CompactDropdown title="Filter by: Retry status" options={['Initial attempt', 'Retry']} selectedOptions={retryStatuses} onApply={selected => handleApplyFilter(() => setRetryStatuses(selected))} onBack={handleBackToMenu} isInline data-id="element-697" />}
            </div>}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-698">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-699">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-700" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-701">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-702">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-703">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-704" />
            <span className="text-base text-gray-900" data-id="element-705">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-706">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-707">
          Apply
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-708">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-709">
      {content}
    </div>;
}
// Searchable Dropdown Component (for card country and currency)
interface SearchableDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack?: (() => void) | null;
  isInline?: boolean;
}
function SearchableDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: SearchableDropdownProps) {
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200" data-id="element-710">
        <div className="flex items-center gap-3 mb-2" data-id="element-711">
          {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-712">
              <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-713" />
            </button>}
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-714">{title}</h3>
        </div>
        <div className="relative" data-id="element-715">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-716" />
          <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-717" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-718">
        {filteredOptions.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-719">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-720" />
            <span className="text-sm text-gray-900" data-id="element-721">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-722">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-723">
          Apply
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-724">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-725">
      {content}
    </div>;
}